import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  LineChart, Line, AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  ComposedChart, Scatter, ScatterChart, ZAxis
} from 'recharts';
import { 
  Truck, Package, MapPin, Clock, AlertTriangle, CheckCircle2, 
  Navigation, Route, Globe, Satellite, Radio, Wifi, Signal,
  ArrowUpRight, ArrowDownRight, RefreshCw, Filter, Search,
  Calendar, Users, DollarSign, Zap, Target, Activity
} from 'lucide-react';

const LogisticsTab = ({ timeRange }) => {
  const [selectedShipment, setSelectedShipment] = useState(null);
  const [isTrackingVisible, setIsTrackingVisible] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');

  // Mock data for logistics
  const shipments = [
    {
      id: 'SH-001',
      origin: 'Riyadh Hub',
      destination: 'Jeddah Store',
      status: 'in-transit',
      progress: 65,
      driver: 'Ahmed Al-Rashid',
      vehicle: 'Truck #TRK-789',
      estimatedArrival: '2024-03-20 14:30',
      currentLocation: { lat: 24.7136, lng: 46.6753 },
      route: [
        { lat: 24.7136, lng: 46.6753, time: '09:00', status: 'completed' },
        { lat: 24.7136, lng: 46.6753, time: '10:30', status: 'completed' },
        { lat: 24.7136, lng: 46.6753, time: '12:00', status: 'current' },
        { lat: 21.4858, lng: 39.1925, time: '14:30', status: 'pending' }
      ],
      items: [
        { name: 'Electronics', quantity: 50, weight: '500kg' },
        { name: 'Clothing', quantity: 200, weight: '300kg' }
      ]
    },
    {
      id: 'SH-002',
      origin: 'Dammam Port',
      destination: 'Riyadh Hub',
      status: 'delivered',
      progress: 100,
      driver: 'Mohammed Al-Sayed',
      vehicle: 'Truck #TRK-456',
      estimatedArrival: '2024-03-19 16:00',
      currentLocation: { lat: 24.7136, lng: 46.6753 },
      route: [
        { lat: 26.4207, lng: 50.0888, time: '08:00', status: 'completed' },
        { lat: 24.7136, lng: 46.6753, time: '16:00', status: 'completed' }
      ],
      items: [
        { name: 'Machinery', quantity: 10, weight: '2000kg' }
      ]
    },
    {
      id: 'SH-003',
      origin: 'Jeddah Port',
      destination: 'Makkah Store',
      status: 'pending',
      progress: 0,
      driver: 'Omar Al-Hassan',
      vehicle: 'Truck #TRK-123',
      estimatedArrival: '2024-03-21 10:00',
      currentLocation: { lat: 21.4858, lng: 39.1925 },
      route: [
        { lat: 21.4858, lng: 39.1925, time: '08:00', status: 'pending' },
        { lat: 21.4225, lng: 39.8262, time: '10:00', status: 'pending' }
      ],
      items: [
        { name: 'Food Items', quantity: 100, weight: '800kg' }
      ]
    }
  ];

  const logisticsMetrics = [
    { month: 'Jan', deliveries: 120, onTime: 95, delays: 5, cost: 45000 },
    { month: 'Feb', deliveries: 135, onTime: 92, delays: 8, cost: 48000 },
    { month: 'Mar', deliveries: 110, onTime: 88, delays: 12, cost: 42000 },
    { month: 'Apr', deliveries: 160, onTime: 96, delays: 4, cost: 52000 },
    { month: 'May', deliveries: 140, onTime: 94, delays: 6, cost: 47000 },
    { month: 'Jun', deliveries: 180, onTime: 98, delays: 2, cost: 58000 }
  ];

  const vehicleFleet = [
    { id: 'TRK-789', type: 'Heavy Truck', status: 'active', location: 'Riyadh', driver: 'Ahmed Al-Rashid', fuel: 85 },
    { id: 'TRK-456', type: 'Medium Truck', status: 'maintenance', location: 'Dammam', driver: 'Mohammed Al-Sayed', fuel: 45 },
    { id: 'TRK-123', type: 'Light Truck', status: 'active', location: 'Jeddah', driver: 'Omar Al-Hassan', fuel: 92 },
    { id: 'TRK-321', type: 'Refrigerated', status: 'active', location: 'Makkah', driver: 'Khalid Al-Otaibi', fuel: 78 }
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'in-transit': return 'text-blue-600 bg-blue-50 border-blue-200';
      case 'delivered': return 'text-green-600 bg-green-50 border-green-200';
      case 'pending': return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'delayed': return 'text-red-600 bg-red-50 border-red-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'in-transit': return <Navigation className="w-4 h-4" />;
      case 'delivered': return <CheckCircle2 className="w-4 h-4" />;
      case 'pending': return <Clock className="w-4 h-4" />;
      case 'delayed': return <AlertTriangle className="w-4 h-4" />;
      default: return <Package className="w-4 h-4" />;
    }
  };

  const getVehicleStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-500';
      case 'maintenance': return 'bg-yellow-500';
      case 'inactive': return 'bg-red-500';
      default: return 'bg-gray-500';
    }
  };

  return (
    <div className="p-8 space-y-8">
      {/* Real-time Tracking Header */}
      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6 }}
        className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-3xl p-6 text-white"
      >
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold mb-2">Live Logistics Tracking</h2>
            <p className="text-blue-100">Real-time monitoring of all shipments and vehicles</p>
          </div>
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse"></div>
              <span className="text-sm">Live Updates</span>
            </div>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="p-3 bg-white/10 backdrop-blur-sm rounded-xl border border-white/20 hover:bg-white/20 transition-all duration-300"
            >
              <RefreshCw className="w-5 h-5" />
            </motion.button>
          </div>
        </div>
      </motion.div>

      {/* Shipments Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Active Shipments */}
        <motion.div
          initial={{ x: -50, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ duration: 0.6 }}
          className="bg-white/60 backdrop-blur-sm rounded-3xl p-6 shadow-xl border border-white/20"
        >
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-xl font-bold text-gray-900">Active Shipments</h3>
              <p className="text-gray-600">Real-time tracking and status</p>
            </div>
            <div className="flex items-center space-x-2">
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="px-3 py-2 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Status</option>
                <option value="in-transit">In Transit</option>
                <option value="pending">Pending</option>
                <option value="delivered">Delivered</option>
              </select>
            </div>
          </div>

          <div className="space-y-4">
            {shipments
              .filter(shipment => filterStatus === 'all' || shipment.status === filterStatus)
              .map((shipment, index) => (
              <motion.div
                key={shipment.id}
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: index * 0.1, duration: 0.5 }}
                onClick={() => setSelectedShipment(shipment)}
                className={`p-4 rounded-2xl border cursor-pointer transition-all duration-300 hover:shadow-lg ${
                  selectedShipment?.id === shipment.id 
                    ? 'border-blue-500 bg-blue-50' 
                    : 'border-gray-200 bg-white/50 hover:bg-white/80'
                }`}
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center space-x-3">
                    <div className={`p-2 rounded-xl ${getStatusColor(shipment.status).split(' ')[2]}`}>
                      {getStatusIcon(shipment.status)}
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900">{shipment.id}</h4>
                      <p className="text-sm text-gray-600">{shipment.driver}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-medium text-gray-900">{shipment.vehicle}</div>
                    <div className="text-xs text-gray-500">{shipment.estimatedArrival}</div>
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">From: {shipment.origin}</span>
                    <span className="text-gray-600">To: {shipment.destination}</span>
                  </div>
                  
                  <div className="relative">
                    <div className="flex justify-between text-xs text-gray-500 mb-1">
                      <span>Progress</span>
                      <span>{shipment.progress}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <motion.div
                        className="bg-gradient-to-r from-blue-500 to-purple-500 h-2 rounded-full"
                        initial={{ width: 0 }}
                        animate={{ width: `${shipment.progress}%` }}
                        transition={{ duration: 1, delay: index * 0.1 }}
                      />
                    </div>
                  </div>

                  <div className="flex justify-between text-xs">
                    {shipment.items.map((item, idx) => (
                      <span key={idx} className="text-gray-600">
                        {item.name}: {item.quantity}
                      </span>
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Vehicle Fleet */}
        <motion.div
          initial={{ x: 50, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ duration: 0.6 }}
          className="bg-white/60 backdrop-blur-sm rounded-3xl p-6 shadow-xl border border-white/20"
        >
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-xl font-bold text-gray-900">Vehicle Fleet</h3>
              <p className="text-gray-600">Fleet status and monitoring</p>
            </div>
            <Truck className="w-6 h-6 text-blue-600" />
          </div>

          <div className="space-y-4">
            {vehicleFleet.map((vehicle, index) => (
              <motion.div
                key={vehicle.id}
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: index * 0.1, duration: 0.5 }}
                className="p-4 bg-white/50 rounded-2xl border border-gray-200 hover:bg-white/80 transition-all duration-300"
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center space-x-3">
                    <div className={`w-3 h-3 rounded-full ${getVehicleStatusColor(vehicle.status)}`}></div>
                    <div>
                      <h4 className="font-semibold text-gray-900">{vehicle.id}</h4>
                      <p className="text-sm text-gray-600">{vehicle.type}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-medium text-gray-900">{vehicle.driver}</div>
                    <div className="text-xs text-gray-500">{vehicle.location}</div>
                  </div>
                </div>

                <div className="flex justify-between items-center">
                  <div className="flex items-center space-x-2">
                    <Zap className="w-4 h-4 text-yellow-500" />
                    <span className="text-sm text-gray-600">Fuel: {vehicle.fuel}%</span>
                  </div>
                  <div className={`px-2 py-1 rounded-full text-xs font-medium ${
                    vehicle.status === 'active' ? 'bg-green-100 text-green-700' :
                    vehicle.status === 'maintenance' ? 'bg-yellow-100 text-yellow-700' :
                    'bg-red-100 text-red-700'
                  }`}>
                    {vehicle.status}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>

      {/* Performance Metrics */}
      <motion.div
        initial={{ y: 50, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6, delay: 0.3 }}
        className="bg-white/60 backdrop-blur-sm rounded-3xl p-6 shadow-xl border border-white/20"
      >
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-xl font-bold text-gray-900">Logistics Performance</h3>
            <p className="text-gray-600">Delivery metrics and trends</p>
          </div>
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2 text-green-600">
              <ArrowUpRight className="w-4 h-4" />
              <span className="text-sm font-medium">+12.5%</span>
            </div>
          </div>
        </div>

        <ResponsiveContainer width="100%" height={300}>
          <ComposedChart data={logisticsMetrics}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey="month" stroke="#6b7280" />
            <YAxis stroke="#6b7280" />
            <Tooltip 
              contentStyle={{ 
                backgroundColor: 'rgba(255, 255, 255, 0.95)', 
                border: 'none', 
                borderRadius: '12px',
                boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)'
              }}
            />
            <Legend />
            <Area 
              type="monotone" 
              dataKey="deliveries" 
              stackId="1" 
              stroke="#3B82F6" 
              fill="#3B82F6" 
              fillOpacity={0.3}
            />
            <Line 
              type="monotone" 
              dataKey="onTime" 
              stroke="#10B981" 
              strokeWidth={3}
              dot={{ fill: '#10B981', strokeWidth: 2, r: 4 }}
            />
            <Bar dataKey="delays" fill="#EF4444" radius={[4, 4, 0, 0]} />
          </ComposedChart>
        </ResponsiveContainer>
      </motion.div>

      {/* Route Visualization */}
      {selectedShipment && (
        <motion.div
          initial={{ y: 50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.6 }}
          className="bg-white/60 backdrop-blur-sm rounded-3xl p-6 shadow-xl border border-white/20"
        >
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-xl font-bold text-gray-900">Route Tracking</h3>
              <p className="text-gray-600">Shipment: {selectedShipment.id}</p>
            </div>
            <button
              onClick={() => setSelectedShipment(null)}
              className="px-4 py-2 bg-gray-600 text-white rounded-xl hover:bg-gray-700 transition-colors"
            >
              Close
            </button>
          </div>

          <div className="relative h-64 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl overflow-hidden">
            {/* Route Line */}
            <svg className="absolute inset-0 w-full h-full">
              <motion.path
                d={`M ${selectedShipment.route[0].lat * 2} ${selectedShipment.route[0].lng * 2} L ${selectedShipment.route[selectedShipment.route.length - 1].lat * 2} ${selectedShipment.route[selectedShipment.route.length - 1].lng * 2}`}
                stroke="#3B82F6"
                strokeWidth="3"
                strokeDasharray="10,5"
                fill="none"
                initial={{ pathLength: 0 }}
                animate={{ pathLength: 1 }}
                transition={{ duration: 2, delay: 0.5 }}
              />
            </svg>

            {/* Route Points */}
            {selectedShipment.route.map((point, index) => (
              <motion.div
                key={index}
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 0.8 + index * 0.2, duration: 0.5 }}
                className="absolute transform -translate-x-1/2 -translate-y-1/2"
                style={{ 
                  left: `${point.lat * 2}%`, 
                  top: `${point.lng * 2}%` 
                }}
              >
                <div className={`w-6 h-6 rounded-full border-4 border-white shadow-lg flex items-center justify-center ${
                  point.status === 'completed' ? 'bg-green-500' :
                  point.status === 'current' ? 'bg-blue-500' :
                  'bg-gray-300'
                }`}>
                  {point.status === 'completed' && <CheckCircle2 className="w-3 h-3 text-white" />}
                  {point.status === 'current' && <Navigation className="w-3 h-3 text-white" />}
                </div>
                <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-2 text-xs text-gray-600">
                  {point.time}
                </div>
              </motion.div>
            ))}
          </div>

          <div className="grid grid-cols-3 gap-4 mt-6">
            {[
              { label: 'Origin', value: selectedShipment.origin, icon: MapPin },
              { label: 'Destination', value: selectedShipment.destination, icon: Target },
              { label: 'Driver', value: selectedShipment.driver, icon: Users }
            ].map((info, index) => (
              <motion.div
                key={info.label}
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 1.2 + index * 0.1, duration: 0.5 }}
                className="text-center p-4 bg-white/50 rounded-xl"
              >
                <info.icon className="w-5 h-5 text-blue-600 mx-auto mb-2" />
                <div className="text-sm font-medium text-gray-900">{info.label}</div>
                <div className="text-xs text-gray-600">{info.value}</div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default LogisticsTab; 